import type { OrderFilters, TripFilters } from './types';

/**
 * Ключовете за TanStack Query. Подредени йерархично, за да може едно
 * `invalidateQueries({ queryKey: qk.orders.all })` да опресни целия раздел
 * след промяна на състояние.
 */
export const qk = {
  session: ['session'] as const,
  org: ['org'] as const,

  board: {
    all: ['board'] as const,
    snapshot: () => ['board', 'snapshot'] as const,
  },

  orders: {
    all: ['orders'] as const,
    list: (filters: OrderFilters) => ['orders', 'list', filters] as const,
    detail: (id: string) => ['orders', 'detail', id] as const,
  },

  trips: {
    all: ['trips'] as const,
    list: (filters: TripFilters) => ['trips', 'list', filters] as const,
    detail: (id: string) => ['trips', 'detail', id] as const,
    route: (id: string) => ['trips', 'route', id] as const,
    sheet: (id: string) => ['trips', 'sheet', id] as const,
    ofVehicle: (vehicleId: string) => ['trips', 'of-vehicle', vehicleId] as const,
  },

  dispatch: {
    all: ['dispatch'] as const,
    candidates: (orderId: string) => ['dispatch', 'candidates', orderId] as const,
  },

  quotes: {
    all: ['quotes'] as const,
    list: () => ['quotes', 'list'] as const,
    calculation: (payload: unknown) => ['quotes', 'calculation', payload] as const,
  },

  partners: {
    all: ['partners'] as const,
    list: (q?: string) => ['partners', 'list', q ?? ''] as const,
    detail: (id: string) => ['partners', 'detail', id] as const,
  },

  places: {
    all: ['places'] as const,
    suggest: (q: string, country?: string) => ['places', 'suggest', q, country ?? ''] as const,
  },

  vehicles: {
    all: ['vehicles'] as const,
    list: () => ['vehicles', 'list'] as const,
    detail: (id: string) => ['vehicles', 'detail', id] as const,
    live: () => ['vehicles', 'live'] as const,
  },

  drivers: {
    all: ['drivers'] as const,
    list: () => ['drivers', 'list'] as const,
    detail: (id: string) => ['drivers', 'detail', id] as const,
  },

  anomalies: {
    all: ['anomalies'] as const,
    list: (includeAcked: boolean) => ['anomalies', 'list', includeAcked] as const,
  },

  /** Сервизът — бусове, сигнали, ремонти, части. */
  maintenance: {
    all: ['maintenance'] as const,
    overview: () => ['maintenance', 'overview'] as const,
    checks: (filter: string, vehicleId?: string) => ['maintenance', 'checks', filter, vehicleId ?? 'all'] as const,
    repairs: (status: string) => ['maintenance', 'repairs', status] as const,
    repair: (id: string) => ['maintenance', 'repair', id] as const,
    shopping: () => ['maintenance', 'shopping'] as const,
  },

  users: {
    all: ['users'] as const,
    list: (includeInactive: boolean) => ['users', 'list', includeInactive] as const,
  },

  /** Документите по имейл — настройките и дневникът. */
  exports: {
    all: ['document-exports'] as const,
    settings: () => ['document-exports', 'settings'] as const,
    log: (orderId?: string) => ['document-exports', 'log', orderId ?? 'all'] as const,
  },

  documents: {
    all: ['documents'] as const,
    queue: (status: string) => ['documents', 'queue', status] as const,
    of: (filter: Record<string, string>) => ['documents', 'of', filter] as const,
    detail: (id: string) => ['documents', 'detail', id] as const,
  },

  geofences: {
    all: ['geofences'] as const,
    list: () => ['geofences', 'list'] as const,
  },

  reports: {
    all: ['reports'] as const,
    summary: (from: string, to: string) => ['reports', 'summary', from, to] as const,
  },

  search: (q: string) => ['search', q] as const,
} as const;
